import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Sidebar from './components/layout/Sidebar.jsx';
import TopBar from './components/layout/TopBar.jsx';
import RegimeBanner from './components/layout/RegimeBanner.jsx';
import PortfolioCommand from './pages/PortfolioCommand.jsx';
import MacroRadar from './pages/MacroRadar.jsx';
import FuturesSignals from './pages/FuturesSignals.jsx';
import SignalConvergence from './pages/SignalConvergence.jsx';
import MnemosynePage from './pages/MnemosynePage.jsx';
import HistoryPage from './pages/HistoryPage.jsx';
import CurrencyIntelligence from './pages/CurrencyIntelligence.jsx';
import TelegramFeed from './pages/TelegramFeed.jsx';
import VoicesPage from './pages/VoicesPage.jsx';
import SmartMoneyPage from './pages/SmartMoneyPage.jsx';
import GeoIntelPage from './pages/GeoIntelPage.jsx';
import GeoTranslator from './pages/GeoTranslator.jsx';
import ModulePerformancePage from './pages/ModulePerformancePage.jsx';
import KairosPerformancePage from './pages/KairosPerformancePage.jsx';
import ScenarioEngine from './pages/ScenarioEngine.jsx';
import OperationsPage from './pages/OperationsPage.jsx';
import RiskEngine from './pages/RiskEngine.jsx';
import NarrativeDivergence from './pages/NarrativeDivergence.jsx';
import TechScout from './pages/TechScout.jsx';
import ButterflyEngine from './pages/ButterflyEngine.jsx';
import PolymarketCuratePage from './pages/PolymarketCuratePage.jsx';
import WatchlistPage from './pages/WatchlistPage.jsx';
import SettingsPage from './pages/SettingsPage.jsx';
import CredibilityPage from './pages/CredibilityPage.jsx';
import SignalQueuePage from './pages/SignalQueuePage.jsx';
import ThemesPage from './pages/ThemesPage.jsx';
import CryptoPage from './pages/CryptoPage.jsx';
import FixedIncomePage from './pages/FixedIncomePage.jsx';
import AgentsPage from './pages/AgentsPage.jsx';
import AlertsPage from './pages/AlertsPage.jsx';
import HoldingsPage from './pages/HoldingsPage.jsx';
import WealthCommand from './pages/WealthCommand.jsx';
import EquityCommand from './pages/command/EquityCommand.jsx';
import FixedIncomeCommand from './pages/command/FixedIncomeCommand.jsx';
import CryptoCommand from './pages/command/CryptoCommand.jsx';
import MacroCommand from './pages/command/MacroCommand.jsx';
import OptionsCommand from './pages/command/OptionsCommand.jsx';
import CostsPage from './pages/system/CostsPage.jsx';
import Login from './pages/Login.jsx';
import KairosGlobalChat from './components/KairosGlobalChat.jsx';
import { PageProvider } from './contexts/PageContext.jsx';
import { getToken, clearToken } from './utils/api.js';

function AuthenticatedApp({ onLogout }) {
  return (
    <PageProvider>
      <BrowserRouter>
        <div className="flex h-screen bg-korinth-bg">
          <Sidebar />
          {/* pt-12 on mobile leaves room for the hamburger bar; lg+ reset */}
          <div className="flex-1 flex flex-col overflow-hidden pt-12 lg:pt-0">
            <RegimeBanner />
            <TopBar onLogout={onLogout} />
            <main className="flex-1 overflow-auto p-3 md:p-6 pb-24">
              <Routes>
                <Route path="/" element={<PortfolioCommand />} />
                <Route path="/portfolio-command" element={<PortfolioCommand />} />
                <Route path="/macro-radar" element={<MacroRadar />} />
                <Route path="/futures" element={<FuturesSignals />} />
                <Route path="/signals" element={<SignalConvergence />} />
              <Route path="/mnemosyne" element={<MnemosynePage />} />
              <Route path="/history" element={<HistoryPage />} />
              <Route path="/currency" element={<CurrencyIntelligence />} />
              <Route path="/telegram" element={<TelegramFeed />} />
              <Route path="/voices" element={<VoicesPage />} />
              <Route path="/smart-money" element={<SmartMoneyPage />} />
              <Route path="/geo-intel" element={<GeoIntelPage />} />
              {/* Sprint 22.7.34: Geo-to-Portfolio Translator — closes the dead /geo sidebar link */}
              <Route path="/geo" element={<GeoTranslator />} />
              <Route path="/module-performance" element={<ModulePerformancePage />} />
              <Route path="/kairos-performance" element={<KairosPerformancePage />} />
              <Route path="/scenarios" element={<ScenarioEngine />} />
              <Route path="/operations" element={<OperationsPage />} />
              <Route path="/risk" element={<RiskEngine />} />
              <Route path="/narrative" element={<NarrativeDivergence />} />
              <Route path="/scout" element={<TechScout />} />
              <Route path="/butterfly" element={<ButterflyEngine />} />
              <Route path="/admin/polymarket-curate" element={<PolymarketCuratePage />} />
              <Route path="/watchlist" element={<WatchlistPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/credibility" element={<CredibilityPage />} />
              <Route path="/signal-queue" element={<SignalQueuePage />} />
              <Route path="/themes" element={<ThemesPage />} />
              <Route path="/crypto" element={<CryptoPage />} />
              {/* Sprint 21.7.2: /fixed-income redirects to new Command Center.
                  Legacy FixedIncomePage preserved at /fixed-income/legacy for one
                  release in case the new surface needs anything back. */}
              <Route path="/fixed-income" element={<Navigate to="/command/fixed-income" replace />} />
              <Route path="/fixed-income/legacy" element={<FixedIncomePage />} />
              {/* Sprint 21.7.2: Domain Command Centers (ADR-027) */}
              <Route path="/command/equity" element={<EquityCommand />} />
              <Route path="/command/fixed-income" element={<FixedIncomeCommand />} />
              <Route path="/command/crypto" element={<CryptoCommand />} />
              <Route path="/command/macro" element={<MacroCommand />} />
              {/* Sprint 21.11: JANUS's Command Center — /options promoted from stub */}
              <Route path="/command/options" element={<OptionsCommand />} />
              <Route path="/options" element={<Navigate to="/command/options" replace />} />
              {/* Sprint 21.7.6 Issue 13: restore operator-tier cost dashboard (Sprint 3.5 infra) */}
              <Route path="/system/costs" element={<CostsPage />} />
              <Route path="/agents" element={<AgentsPage />} />
              {/* Sprint 21.7.7 Issue 1b: /pantheon alias — sidebar links to /agents but
                  documentation + URLs referenced /pantheon as the canonical route. */}
              <Route path="/pantheon" element={<AgentsPage />} />
              <Route path="/alerts" element={<AlertsPage />} />
              <Route path="/holdings" element={<HoldingsPage />} />
              {/* Sprint 22.7.30: Wealth Command spec engine — closes the dead /wealth sidebar link */}
              <Route path="/wealth" element={<WealthCommand />} />
              </Routes>
            </main>
          </div>
          <KairosGlobalChat />
        </div>
      </BrowserRouter>
    </PageProvider>
  );
}

export default function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = getToken();
    if (!token) { setChecking(false); return; }
    fetch('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (res.ok) setAuthenticated(true); else clearToken(); })
      .catch(() => clearToken())
      .finally(() => setChecking(false));
  }, []);

  if (checking) return <div className="min-h-screen bg-korinth-bg flex items-center justify-center"><div className="text-korinth-muted font-mono text-sm">Loading...</div></div>;
  if (!authenticated) return <Login onLogin={() => setAuthenticated(true)} />;
  return <AuthenticatedApp onLogout={() => { clearToken(); setAuthenticated(false); }} />;
}
